<template>
  <div
    :class="[
      'input-controls',
      `type--${genericType}`,
      !showFilterInput && type !== 'text' ? 'pa-0' : '',
    ]"
  >
    <v-btn v-show="!showFilterInput" icon @click="enableFilter">
      <span>
        {{ filterLabel.text }}
      </span>
      <v-icon class="material-icons">
        {{ filterLabel.icon }}
      </v-icon>
    </v-btn>
    <div v-show="showFilterInput" class="input-boxes">
      <global-dropdown
        v-if="type !== 'text'"
        v-model="selectedFilter"
        class="filter-type-dropdown"
        :options="filterTypeOptions"
        :size="24"
        :min-width="24"
        @change="changeFilterType"
      />
      <template v-if="isDateType">
        <calendar-popup
          ref="datePicker"
          :value="value"
          min-date="1900-01-01"
          @input="updateValue"
        >
          <input
            disabled
            :value="value"
            class="filter-input-display"
            autocomplete="off"
          />
        </calendar-popup>
      </template>
      <template v-else>
        <input
          ref="filterBox"
          :value="value"
          :type="type"
          class="filter-input-display"
          autocomplete="off"
          @keyup.enter="(e) => updateValue(e.target.value)"
          @blur="(e) => updateValue(e.target.value)"
        />
      </template>
      <v-icon
        class="clear-filter"
        title="Clear input field"
        dense
        @click="closeFilter"
      >
        close
      </v-icon>
    </div>
  </div>
</template>

<script>
import CalendarPopup from '../CalendarPopup.vue';
import GlobalDropdown from '../GlobalDropdown.vue';
import types from '../../static/tables/types.js';

const filterTypes = ['equal', 'less', 'greater', 'empty'];

export default {
  name: 'MultiTypeFilterInput',
  components: {
    CalendarPopup,
    GlobalDropdown,
  },
  props: {
    value: {
      type: [String, Number, Date],
      default: undefined,
    },
    type: {
      type: String,
      default: 'text',
      validator: (t) => Object.values(types).includes(t),
    },
    filterType: {
      type: String,
      default: 'equal',
      validator: (ft) => filterTypes.includes(ft),
    },
  },
  data() {
    return {
      showFilterInput: false,
      selectedFilter: 'equal',
      filterByTypes: [
        {
          label: '=',
          value: 'equal',
        },
        {
          label: '<',
          value: 'less',
        },
        {
          label: '>',
          value: 'greater',
        },
      ],
    };
  },
  computed: {
    filterTypeOptions() {
      return this.filterByTypes.map((filterType, index) => ({
        id: `multi-filter-type-${index}`,
        value: filterType.value,
        label: filterType.label,
      }));
    },
    isDateType() {
      return ['date', 'dateTime', 'datetime'].includes(this.type);
    },
    genericType() {
      if (this.isDateType) return 'date';
      return this.type;
    },
    filterLabel() {
      return {
        text: this.isDateType ? 'Select date' : 'Search',
        icon: this.isDateType ? 'date_range' : 'search',
      };
    },
  },
  mounted() {
    this.selectedFilter = this.filterType;
    if (this.value !== undefined) {
      this.showFilterInput = true;
    }
  },
  updated() {
    this.selectedFilter = this.filterType;
  },
  methods: {
    enableFilter() {
      this.showFilterInput = true;
      this.$nextTick(this.focusInput);
    },
    focusInput() {
      if (this.isDateType) {
        this.$refs.datePicker.focusMenu();
      } else {
        this.$refs.filterBox.focus();
      }
    },
    changeFilterType(filterType) {
      this.$emit('updated', {
        filterType,
        value: this.value,
      });
    },
    closeFilter() {
      this.showFilterInput = false;
      this.updateValue(undefined);
    },
    updateValue(value) {
      if (value === '') {
        value = undefined;
      }
      this.$emit('updated', {
        filterType: this.filterType,
        value,
      });
    },
    updateOnEnter(e) {
      let value = e.target.value ? e.target.value : undefined;
      this.updateValue(value);
    },
  },
};
</script>

<style>
.filter-header__dense .input-controls {
  display: flex;
  align-items: center;
  align-content: center;
  position: absolute;
  bottom: 0;
  width: calc(100% + 1px);
  margin-left: -1px;
  padding-left: 12px;
  background-color: var(--sb-table-filter-input-bg, #fff);
  border-bottom: 1px solid var(--border, #e2e8f0);
  margin-top: 10px;
  min-height: 33px;
  max-height: 33px;
  left: 0;
}
.input-boxes {
  width: calc(100% - 20px);
  padding-right: 10px;
}
.input-boxes .clear-filter {
  color: var(--text2, #475569) !important;
  margin-bottom: -3px;
  margin-left: -2px;
}
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.filter-header__dense input:not([readonly='readonly']) {
  padding-left: 4px;
  outline: none;
  height: 24px;
  border: 1px solid var(--sb-table-filter-input-bdr, #e2e8f0);
  top: 0;
  width: 100%;
  color: var(--text, #0f172a);
  background-color: var(--sb-table-filter-input-bg, #fff);
}
.input-boxes [role='combobox'] .v-select__slot {
  flex-wrap: wrap;
}
.input-boxes .filter-type-dropdown,
.input-boxes .v-text-field__details {
  display: none;
}
.input-boxes .filter-type-dropdown {
  display: inline-block;
  width: 25px;
  margin: 0 -10px;
}
.input-boxes .filter-type-dropdown .sb-global-dropdown__trigger {
  width: 25px;
  min-width: 25px;
  padding: 0 0 0 3px;
  border: 0;
  background: transparent;
  box-shadow: none;
}
.input-boxes .filter-type-dropdown .sb-global-dropdown__label {
  margin-bottom: 5px;
}
.input-boxes .filter-type-dropdown .sb-global-dropdown__chevron {
  display: none;
}
.input-boxes .v-list__tile {
  height: auto;
  padding: 10px;
}
.v-menu__content .v-select-list a.v-list__tile.v-list__tile--link.theme--light {
  font-size: 14px;
  height: auto;
  padding: 8px 15px;
}
.input-boxes [role='combobox'] {
  width: 25px;
  height: auto;
  display: inline-block;
  padding: 0;
  margin: 0 -10px;
  background: none;
  box-shadow: none;
  border-radius: 0;
}
.input-boxes [role='combobox'] .v-input__slot {
  margin-bottom: 0;
}
.input-boxes [role='combobox'] .v-select__selections {
  justify-content: center;
}
.input-boxes [role='combobox'] .v-select__selection--comma {
  margin: 0;
  margin-bottom: 5px;
  padding-left: 3px;
}
.input-controls.type--date
  .input-boxes
  [role='combobox']
  .v-select__selection--comma {
  padding-left: 4px;
}
.input-controls.type--date .v-btn__content > span,
.input-controls.type--number .v-btn__content > span {
  padding-left: 10px;
}

.input-boxes [role='combobox'] .v-input__append-inner {
  display: none;
}
.input-boxes [role='combobox'] .v-input__slot::before,
.input-boxes [role='combobox'] .v-input__slot::after {
  content: none !important;
}

.sb-base-table2 .filter-header__dense .input-boxes input {
  height: auto;
  vertical-align: middle;
  margin: 6px auto;
  display: inline-block;
}
.sb-base-table2 .filter-header__dense input {
  width: 100%;
  vertical-align: bottom;
}
</style>
